import React from 'react';
import { View, ScrollView, type StyleProp, type ViewStyle } from 'react-native';
import { useTheme } from '../theme';

interface BoxProps {
  children?: React.ReactNode;
  gap?: number;
  style?: StyleProp<ViewStyle>;
  wrap?: boolean;
}

/** Horizontal flex, centered cross-axis — replaces .t-row (gap defaults to 8). */
export function Row({ children, gap = 8, wrap, style }: BoxProps) {
  return (
    <View style={[{ flexDirection: 'row', alignItems: 'center', gap, flexWrap: wrap ? 'wrap' : 'nowrap' }, style]}>
      {children}
    </View>
  );
}

/** Vertical flex — replaces .t-col (gap 8) / .t-stack (pass gap={12}). */
export function Col({ children, gap = 8, style }: BoxProps) {
  return <View style={[{ flexDirection: 'column', gap }, style]}>{children}</View>;
}

/** Row with space-between — replaces .t-spread. */
export function Spread({ children, gap = 12, style }: BoxProps) {
  return (
    <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap }, style]}>
      {children}
    </View>
  );
}

/** Wrapping row of chips/buttons — replaces .t-cluster. */
export function Cluster({ children, gap = 8, style }: BoxProps) {
  return (
    <View style={[{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap }, style]}>
      {children}
    </View>
  );
}

/** flex:1 wrapper — replaces .t-grow. */
export function Grow({ children, style }: BoxProps) {
  return <View style={[{ flex: 1, minWidth: 0 }, style]}>{children}</View>;
}

/** Scrollable screen body with the prototype's paper background + standard padding. */
export function Screen({ children, style, padding = 20 }: BoxProps & { padding?: number }) {
  const t = useTheme();
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: t.colors.paper }}
      contentContainerStyle={[{ padding, gap: 16 }, style]}
    >
      {children}
    </ScrollView>
  );
}
